import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { TagFilter } from "../Components/TagFilter";
import { TagEditor } from "../Features/Tags/TagEditor";

type TaskStatus = "Todo" | "Doing" | "Blocked" | "Done";
type TaskPriority = "Low" | "Medium" | "High";
type TaskColumn = "status" | "priority" | "due" | "tags";

type Task = {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  due: string;
  tags: string[];
};

const taskStorageKey = "dawndiver.tasks";
const optionalColumns: TaskColumn[] = ["status", "priority", "due", "tags"];
const taskGridTemplateColumns =
  "28px minmax(220px, 1fr) 86px 80px 96px minmax(160px, 0.8fr) 72px";
const columnLabels: Record<TaskColumn, string> = {
  status: "Status",
  priority: "Priority",
  due: "Due",
  tags: "Tags",
};

export const defaultTasks: Task[] = [
  {
    id: "write-task-view",
    title: "Build task list view",
    description: "Create a dense list that can show many tasks at once.",
    status: "Doing",
    priority: "High",
    due: "Today",
    tags: ["frontend", "tasks"],
  },
  {
    id: "tag-filters",
    title: "Filter tasks by tag",
    description: "Allow one or more tags to narrow the visible task list.",
    status: "Todo",
    priority: "High",
    due: "Today",
    tags: ["frontend", "filters"],
  },
  {
    id: "task-detail-page",
    title: "Open task detail page",
    description: "Clicking a task should navigate to its own page.",
    status: "Todo",
    priority: "High",
    due: "Tomorrow",
    tags: ["routing", "tasks"],
  },
  {
    id: "keyboard-actions",
    title: "Add task actions to command palette",
    description: "Expose common task actions through the existing command menu.",
    status: "Todo",
    priority: "Medium",
    due: "This week",
    tags: ["kbar", "productivity"],
  },
  {
    id: "persist-tasks",
    title: "Persist tasks",
    description: "Move task data out of the page component and into durable storage.",
    status: "Blocked",
    priority: "Medium",
    due: "Later",
    tags: ["data", "backend"],
  },
  {
    id: "done-state",
    title: "Design completed state",
    description: "Make completed tasks visible without letting them dominate the list.",
    status: "Done",
    priority: "Low",
    due: "Done",
    tags: ["design", "tasks"],
  },
];

export function Tasks() {
  const [taskItems, setTaskItems] = useState<Task[]>(() => loadTasks());
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [showCompleted, setShowCompleted] = useState(true);
  const [visibleColumns, setVisibleColumns] =
    useState<TaskColumn[]>(optionalColumns);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<TaskStatus>("Todo");
  const [priority, setPriority] = useState<TaskPriority>("Medium");
  const [due, setDue] = useState("");
  const [formTags, setFormTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (!formOpen) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !tagModalOpen) {
        closeTaskForm();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formOpen, tagModalOpen]);

  const allTags = useMemo(
    () => Array.from(new Set(taskItems.flatMap((task) => task.tags))).sort(),
    [taskItems],
  );

  const filteredTasks = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return taskItems.filter((task) =>
      (showCompleted || task.status !== "Done") &&
      selectedTags.every((tag) => task.tags.includes(tag)) &&
      matchesTaskSearch(task, normalizedSearch),
    );
  }, [search, selectedTags, showCompleted, taskItems]);

  function handleSaveTask(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedTitle = title.trim();

    if (!trimmedTitle) {
      return;
    }

    const nextTask = {
      id: editingTaskId ?? createTaskId(trimmedTitle, taskItems),
      title: trimmedTitle,
      description: description.trim() || "No description yet.",
      status,
      priority,
      due: due.trim() || "No due date",
      tags: formTags,
    };

    setTaskItems((currentTasks) => {
      const nextTasks = editingTaskId
        ? currentTasks.map((task) =>
            task.id === editingTaskId ? nextTask : task,
          )
        : [nextTask, ...currentTasks];

      saveTasks(nextTasks);
      return nextTasks;
    });

    closeTaskForm();
  }

  function toggleColumn(column: TaskColumn) {
    setVisibleColumns((currentColumns) =>
      currentColumns.includes(column)
        ? currentColumns.filter((currentColumn) => currentColumn !== column)
        : [...currentColumns, column],
    );
  }

  function openAddForm() {
    resetTaskForm();
    setFormOpen(true);
  }

  function openEditForm(task: Task) {
    setEditingTaskId(task.id);
    setTitle(task.title);
    setDescription(task.description);
    setStatus(task.status);
    setPriority(task.priority);
    setDue(task.due);
    setFormTags(task.tags);
    setTagInput("");
    setFormOpen(true);
  }

  function closeTaskForm() {
    setFormOpen(false);
    setTagModalOpen(false);
    resetTaskForm();
  }

  function resetTaskForm() {
    setEditingTaskId(null);
    setTitle("");
    setDescription("");
    setStatus("Todo");
    setPriority("Medium");
    setDue("");
    setFormTags([]);
    setTagInput("");
  }

  function addFormTag() {
    const tag = tagInput.trim();

    if (!tag) {
      setTagInput("");
      return;
    }

    setFormTags((currentTags) =>
      currentTags.some(
        (currentTag) => currentTag.toLowerCase() === tag.toLowerCase(),
      )
        ? currentTags
        : [...currentTags, tag],
    );
    setTagInput("");
  }

  function removeFormTag(tag: string) {
    setFormTags((currentTags) =>
      currentTags.filter((currentTag) => currentTag !== tag),
    );
  }

  function toggleTaskComplete(task: Task) {
    setTaskItems((currentTasks) => {
      const nextTasks = currentTasks.map((currentTask) =>
        currentTask.id === task.id ? toggleTaskStatus(currentTask) : currentTask,
      );

      saveTasks(nextTasks);
      return nextTasks;
    });
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.heading}>Tasks</h1>
          <p style={styles.subtle}>
            {filteredTasks.length} of {taskItems.length} tasks shown
          </p>
        </div>
        <Button variant="contained" onClick={openAddForm}>
          Add task
        </Button>
      </header>

      {formOpen && (
        <div
          style={styles.modalOverlay}
          role="presentation"
          onClick={closeTaskForm}
        >
          <section
            style={styles.formPanel}
            role="dialog"
            aria-modal="true"
            aria-label="Add task"
            onClick={(event) => event.stopPropagation()}
          >
            <header style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingTaskId ? "Edit task" : "Add task"}
              </h2>
              <Button type="button" onClick={closeTaskForm}>
                Close
              </Button>
            </header>

            <TagEditor
              open={tagModalOpen}
              tagInput={tagInput}
              tags={formTags}
              onOpen={() => setTagModalOpen(true)}
              onClose={() => setTagModalOpen(false)}
              onTagInputChange={setTagInput}
              onAddTag={addFormTag}
              onRemoveTag={removeFormTag}
            />

            <form onSubmit={handleSaveTask}>
              <Stack sx={{ gap: 2 }}>
                <TextField
                  label="Task"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  fullWidth
                  sx={textFieldSx}
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  sx={textFieldSx}
                />
                <Stack direction={{ xs: "column", sm: "row" }} sx={{ gap: 2 }}>
                  <TextField
                    select
                    label="Status"
                    value={status}
                    onChange={(event) =>
                      setStatus(event.target.value as TaskStatus)
                    }
                    fullWidth
                    sx={textFieldSx}
                  >
                    <MenuItem value="Todo">Todo</MenuItem>
                    <MenuItem value="Doing">Doing</MenuItem>
                    <MenuItem value="Blocked">Blocked</MenuItem>
                    <MenuItem value="Done">Done</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Priority"
                    value={priority}
                    onChange={(event) =>
                      setPriority(event.target.value as TaskPriority)
                    }
                    fullWidth
                    sx={textFieldSx}
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </TextField>
                  <TextField
                    label="Due"
                    value={due}
                    onChange={(event) => setDue(event.target.value)}
                    placeholder="Today, Friday, 2026-05-20"
                    fullWidth
                    sx={textFieldSx}
                  />
                </Stack>
                <Stack direction="row" sx={{ gap: 1, justifyContent: "flex-end" }}>
                  <Button type="button" onClick={closeTaskForm}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained">
                    {editingTaskId ? "Save task" : "Add task"}
                  </Button>
                </Stack>
              </Stack>
            </form>
          </section>
        </div>
      )}

      <TextField
        type="search"
        label="Search tasks"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        fullWidth
        sx={{ ...textFieldSx, mb: 2 }}
      />

      <section style={styles.filterBar}>
        <TagFilter
          availableTags={allTags}
          selectedTags={selectedTags}
          onSelectedTagsChange={setSelectedTags}
          resultLimit={50}
        />
        <label style={styles.completedToggle}>
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(event) => setShowCompleted(event.target.checked)}
            style={styles.completedCheckbox}
          />
          Show completed
        </label>
      </section>

      <section style={styles.list} aria-label="Task list">
        <div
          style={{
            ...styles.taskHeaderRow,
            gridTemplateColumns: taskGridTemplateColumns,
          }}
        >
          <span aria-hidden="true" />
          <span>Task</span>
          {optionalColumns.map((column) => {
            const selected = visibleColumns.includes(column);
            return (
              <button
                key={column}
                type="button"
                style={{
                  ...styles.headerButton,
                  ...(column === "tags" ? styles.tagsHeader : {}),
                  ...(selected ? {} : styles.headerButtonDisabled),
                }}
                onClick={() => toggleColumn(column)}
                aria-pressed={selected}
              >
                {columnLabels[column]}
              </button>
            );
          })}
          <span style={styles.actionsHeader}>Actions</span>
        </div>
        {filteredTasks.map((task) => (
          <div
            key={task.id}
            style={{
              ...styles.taskRow,
              gridTemplateColumns: taskGridTemplateColumns,
            }}
          >
            <button
              type="button"
              style={{
                ...styles.completeButton,
                ...(task.status === "Done" ? styles.completeButtonDone : {}),
              }}
              onClick={() => toggleTaskComplete(task)}
              aria-label={
                task.status === "Done"
                  ? `Mark ${task.title} incomplete`
                  : `Mark ${task.title} complete`
              }
            />
            <Link
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              style={{
                ...styles.taskTitleLink,
                ...(task.status === "Done" ? styles.taskTitleDone : {}),
              }}
            >
              {task.title}
            </Link>
            <span style={styles.taskMeta}>
              {visibleColumns.includes("status") ? task.status : ""}
            </span>
            <span style={styles.taskMeta}>
              {visibleColumns.includes("priority") ? task.priority : ""}
            </span>
            <span style={styles.taskMeta}>
              {visibleColumns.includes("due") ? task.due : ""}
            </span>
            <span style={styles.taskTags}>
              {visibleColumns.includes("tags") &&
                task.tags.map((tag) => (
                  <span key={tag} style={styles.tagPill}>
                    {tag}
                  </span>
                ))}
            </span>
            <button
              type="button"
              style={styles.editButton}
              onClick={() => openEditForm(task)}
            >
              Edit
            </button>
          </div>
        ))}
      </section>
    </main>
  );
}

export function TaskDetails() {
  const navigate = useNavigate();
  const { taskId } = useParams({ strict: false }) as { taskId?: string };
  const task = loadTasks().find((currentTask) => currentTask.id === taskId);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        navigate({ to: "/tasks" });
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [navigate]);

  if (!task) {
    return (
      <main style={styles.page}>
        <Link to="/tasks" style={styles.backLink}>
          Back to tasks
        </Link>
        <h1 style={styles.heading}>Task not found</h1>
      </main>
    );
  }

  return (
    <main style={styles.page}>
      <Link to="/tasks" style={styles.backLink}>
        Back to tasks
      </Link>
      <article style={styles.detail}>
        <div style={styles.detailHeader}>
          <h1 style={styles.heading}>{task.title}</h1>
          <span style={styles.statusBadge}>{task.status}</span>
        </div>
        <p style={styles.description}>{task.description}</p>
        <dl style={styles.detailsGrid}>
          <div>
            <dt style={styles.detailLabel}>Priority</dt>
            <dd style={styles.detailValue}>{task.priority}</dd>
          </div>
          <div>
            <dt style={styles.detailLabel}>Due</dt>
            <dd style={styles.detailValue}>{task.due}</dd>
          </div>
          <div>
            <dt style={styles.detailLabel}>Tags</dt>
            <dd style={styles.detailValue}>{task.tags.join(", ")}</dd>
          </div>
        </dl>
      </article>
    </main>
  );
}

function loadTasks(): Task[] {
  if (typeof window === "undefined") {
    return defaultTasks;
  }

  const storedTasks = window.localStorage.getItem(taskStorageKey);

  if (!storedTasks) {
    return defaultTasks;
  }

  try {
    return JSON.parse(storedTasks) as Task[];
  } catch {
    return defaultTasks;
  }
}

function saveTasks(tasks: Task[]) {
  window.localStorage.setItem(taskStorageKey, JSON.stringify(tasks));
}

function toggleTaskStatus(task: Task): Task {
  const nextStatus: TaskStatus = task.status === "Done" ? "Todo" : "Done";

  return {
    ...task,
    status: nextStatus,
  };
}

function createTaskId(title: string, existingTasks: Task[]) {
  const baseId =
    title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "task";
  let id = baseId;
  let suffix = 2;

  while (existingTasks.some((task) => task.id === id)) {
    id = `${baseId}-${suffix}`;
    suffix += 1;
  }

  return id;
}

function matchesTaskSearch(task: Task, normalizedSearch: string) {
  if (!normalizedSearch) {
    return true;
  }

  return [
    task.title,
    task.description,
    task.status,
    task.priority,
    task.due,
    ...task.tags,
  ]
    .join(" ")
    .toLowerCase()
    .includes(normalizedSearch);
}

const styles = {
  page: {
    width: "min(1040px, calc(100vw - 32px))",
    margin: "0 auto",
    padding: "28px 0",
    textAlign: "left",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  heading: {
    margin: 0,
  },
  subtle: {
    marginTop: "4px",
    color: "var(--text)",
    fontSize: "14px",
  },
  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "rgba(0, 0, 0, 0.42)",
    padding: "72px 16px 16px",
    overflowY: "auto",
  },
  formPanel: {
    width: "min(720px, 100%)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    background: "var(--bg)",
    boxShadow: "var(--shadow)",
    padding: "16px",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "16px",
  },
  modalTitle: {
    margin: 0,
    color: "var(--text-h)",
    fontSize: "22px",
    fontWeight: 500,
  },
  filterBar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "14px",
  },
  completedToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    minHeight: "36px",
    color: "var(--text-h)",
    fontSize: "14px",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  completedCheckbox: {
    width: "16px",
    height: "16px",
    accentColor: "var(--accent)",
    cursor: "pointer",
  },
  list: {
    borderTop: "1px solid var(--border)",
  },
  taskHeaderRow: {
    display: "grid",
    alignItems: "center",
    gap: "12px",
    minHeight: "34px",
    borderBottom: "1px solid var(--border)",
    color: "var(--text)",
    fontSize: "12px",
    textTransform: "uppercase",
  },
  headerButton: {
    border: 0,
    background: "transparent",
    color: "inherit",
    padding: 0,
    font: "inherit",
    textAlign: "left",
    textTransform: "inherit",
    cursor: "pointer",
  },
  headerButtonDisabled: {
    opacity: 0.4,
    textDecoration: "line-through",
  },
  taskRow: {
    display: "grid",
    alignItems: "center",
    gap: "12px",
    minHeight: "44px",
    borderBottom: "1px solid var(--border)",
    color: "inherit",
    textDecoration: "none",
  },
  taskTitleLink: {
    color: "var(--text-h)",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textDecoration: "none",
  },
  taskTitleDone: {
    color: "var(--text)",
    textDecoration: "line-through",
  },
  completeButton: {
    width: "18px",
    height: "18px",
    border: "2px solid var(--border)",
    borderRadius: "999px",
    background: "var(--bg)",
    cursor: "pointer",
  },
  completeButtonDone: {
    borderColor: "var(--accent-border)",
    background: "var(--accent)",
    boxShadow: "inset 0 0 0 4px var(--bg)",
  },
  taskMeta: {
    fontSize: "13px",
    color: "var(--text)",
  },
  taskTags: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    justifyContent: "flex-end",
  },
  tagsHeader: {
    textAlign: "right",
  },
  actionsHeader: {
    textAlign: "right",
  },
  editButton: {
    justifySelf: "end",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    background: "var(--bg)",
    color: "var(--text-h)",
    padding: "5px 9px",
    fontSize: "13px",
    cursor: "pointer",
  },
  tagPill: {
    borderRadius: "999px",
    background: "var(--social-bg)",
    color: "var(--text)",
    padding: "2px 7px",
    fontSize: "12px",
    lineHeight: 1.4,
  },
  backLink: {
    display: "inline-flex",
    marginBottom: "18px",
    color: "var(--accent)",
    textDecoration: "none",
    fontSize: "14px",
  },
  detail: {
    borderTop: "1px solid var(--border)",
    paddingTop: "20px",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "18px",
  },
  statusBadge: {
    border: "1px solid var(--accent-border)",
    borderRadius: "999px",
    background: "var(--accent-bg)",
    color: "var(--text-h)",
    padding: "5px 10px",
    fontSize: "13px",
  },
  description: {
    maxWidth: "680px",
    marginBottom: "24px",
    color: "var(--text-h)",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "16px",
    margin: 0,
  },
  detailLabel: {
    color: "var(--text)",
    fontSize: "12px",
    textTransform: "uppercase",
  },
  detailValue: {
    margin: "4px 0 0",
    color: "var(--text-h)",
  },
} satisfies Record<string, CSSProperties>;

const textFieldSx = {
  "& .MuiInputBase-input": {
    color: "var(--text-h)",
  },
  "& .MuiInputBase-root": {
    color: "var(--text-h)",
  },
  "& .MuiInputLabel-root": {
    color: "var(--text)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--border)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "var(--accent-border)",
  },
};
